import React from 'react';
import { Link } from 'react-router-dom';

export const LegalShell = ({ title, lastUpdated, children }) => (
  <div className="min-h-screen relative noise">
    <header className="sticky top-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="legal-home">
          <span className="h-8 w-8 rounded-full bg-gradient-to-br from-bx-cyan via-bx-magenta to-bx-volt breathe" />
          <span className="font-display text-xl font-black tracking-tight">BubbleX</span>
        </Link>
        <nav className="flex gap-4 text-xs uppercase tracking-widest text-white/60">
          <Link to="/terms" data-testid="legal-nav-terms" className="hover:text-white">Terms</Link>
          <Link to="/privacy" data-testid="legal-nav-privacy" className="hover:text-white">Privacy</Link>
          <Link to="/cookies" data-testid="legal-nav-cookies" className="hover:text-white">Cookies</Link>
          <Link to="/about" data-testid="legal-nav-about" className="hover:text-white">About</Link>
          <Link to="/contact" data-testid="legal-nav-contact" className="hover:text-white">Contact</Link>
        </nav>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-4 sm:px-8 py-16">
      <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tighter">{title}</h1>
      {lastUpdated && (
        <div className="text-xs uppercase tracking-widest text-white/60 mt-3">
          Last updated: {lastUpdated}
        </div>
      )}
      <article className="mt-10 prose prose-invert max-w-none text-white/75 leading-relaxed space-y-5">
        {children}
      </article>
    </main>
    <footer className="border-t border-white/5 py-8 px-4 text-center text-[11px] text-white/60 uppercase tracking-widest">
      © {new Date().getFullYear()} BubbleX — All rights reserved.
    </footer>
  </div>
);

const S = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="font-display text-xl sm:text-2xl font-bold text-white mt-8">{title}</h2>
    <div className="text-white/70 text-sm sm:text-base leading-relaxed space-y-3">{children}</div>
  </section>
);

export const Terms = () => (
  <LegalShell title="Terms of Service" lastUpdated="May 14, 2026">
    <p className="text-white/70 text-sm sm:text-base">
      By accessing or using BubbleX (the "Service"), you agree to be bound by these Terms. If you do not agree, do not use the Service.
    </p>
    <S title="1. Eligibility">
      <p>You must be at least 13 years old (or the minimum age in your jurisdiction) to use BubbleX. By creating an account, you confirm you meet this requirement and provide accurate information.</p>
    </S>
    <S title="2. Your Account">
      <p>You are responsible for safeguarding your credentials and for all activity that occurs under your account. Notify us immediately of unauthorized use. We may suspend or terminate accounts that violate these Terms or our community guidelines.</p>
    </S>
    <S title="3. Digital Bubbles & Drops">
      <p>Bubbles are digital collectibles tied to your account and the BubbleX public ledger. Limited "Founder Drops" are sold via Stripe at fixed prices. All sales are recorded; refunds are governed by our refund policy below. A portion of each Drop is routed to a charitable cause selected at checkout.</p>
    </S>
    <S title="4. Acceptable Use">
      <p>You will not harass, dox, threaten, or post unlawful, hateful, or sexually exploitative content. You will not exploit bugs, manipulate energy/leaderboards, or attempt to circumvent moderation. We use automated and human review and may remove content or accounts that violate this policy.</p>
    </S>
    <S title="5. Intellectual Property">
      <p>You retain rights to images you upload. By uploading you grant BubbleX a worldwide, non-exclusive license to display and process them for the purpose of generating Bubble artwork. The BubbleX name, logos, and platform code are owned by BubbleX, Inc.</p>
    </S>
    <S title="6. Payments & Refunds">
      <p>Founder Drops purchases are final once your Bubble has been minted to your account. If a minting error occurs after successful payment, contact <Link to="/contact" className="text-bx-cyan">support</Link> within 14 days for a re-mint or refund. Charity splits are non-refundable once distributed.</p>
    </S>
    <S title="7. No Gambling, No Investment">
      <p>BubbleX is not a lottery, betting service, or financial instrument. Bubbles have no guaranteed monetary value. Do not purchase Bubbles expecting financial return.</p>
    </S>
    <S title="8. Disclaimers">
      <p>The Service is provided "as is" without warranty of any kind. To the maximum extent permitted by law, BubbleX disclaims all implied warranties of merchantability, fitness for a particular purpose, and non-infringement.</p>
    </S>
    <S title="9. Limitation of Liability">
      <p>To the fullest extent permitted by law, BubbleX's aggregate liability for any claim is limited to the amount you paid us in the 12 months preceding the claim, or USD $100, whichever is greater.</p>
    </S>
    <S title="10. Termination">
      <p>You may close your account at any time. We may suspend or terminate your access for violation of these Terms. Upon termination, your right to use the Service ends, though ledger entries remain for transparency.</p>
    </S>
    <S title="11. Changes">
      <p>We may update these Terms. Material changes will be announced in-app. Continued use after the effective date constitutes acceptance.</p>
    </S>
    <S title="12. Contact">
      <p>Questions? <Link to="/contact" className="text-bx-cyan">Reach out here</Link>.</p>
    </S>
  </LegalShell>
);

export const Privacy = () => (
  <LegalShell title="Privacy Policy" lastUpdated="May 14, 2026">
    <p>BubbleX respects your privacy. This Policy explains what we collect, how we use it, and your rights.</p>
    <S title="1. What we collect">
      <ul className="list-disc pl-5 space-y-1">
        <li><b>Account data</b>: email, username, hashed password, profile bio, city, badges.</li>
        <li><b>Content</b>: Bubbles, posts, comments, transfers, reactions, donations.</li>
        <li><b>Optional uploads</b>: face reference images for Bubble generation.</li>
        <li><b>Usage</b>: device, IP, timestamps, basic analytics.</li>
        <li><b>Payment</b>: Stripe handles card data. We store only session ID, amount, and status.</li>
      </ul>
    </S>
    <S title="2. How we use it">
      <ul className="list-disc pl-5 space-y-1">
        <li>Operate, maintain, and improve BubbleX.</li>
        <li>Generate AI personalities and Bubble artwork (via Anthropic, OpenAI, and Google AI providers).</li>
        <li>Moderate content (OpenAI moderation).</li>
        <li>Record ledger events for transparency.</li>
        <li>Detect fraud, abuse, and bots.</li>
      </ul>
    </S>
    <S title="3. AI sub-processors">
      <p>We use Anthropic (Claude), OpenAI (GPT, moderation), and Google (Gemini). Your inputs to AI features are sent to these providers solely to fulfill your request. We do not sell your data.</p>
    </S>
    <S title="4. Sharing">
      <p>We never sell personal data. We share data only with sub-processors needed to operate the Service (hosting, Stripe, AI providers) and where required by law.</p>
    </S>
    <S title="5. Public ledger">
      <p>BubbleX records pseudonymous events (user id, bubble id, hashes) on a public hash-chained ledger to provide verifiable transparency. Your email and password are never on the ledger.</p>
    </S>
    <S title="6. Retention">
      <p>We retain account data while your account is active. You can delete your account from <Link to="/profile" className="text-bx-cyan">Profile</Link>; we will erase profile data within 30 days. Ledger entries are immutable but contain no PII beyond pseudonymous user IDs.</p>
    </S>
    <S title="7. Your rights">
      <p>Subject to applicable law (GDPR/CCPA), you may request access, correction, deletion, or export of your personal data by emailing <a className="text-bx-cyan" href="mailto:privacy@bubblex.app">privacy@bubblex.app</a>.</p>
    </S>
    <S title="8. Children">
      <p>BubbleX is not directed to children under 13. We do not knowingly collect data from children under 13. Contact us to delete any such data.</p>
    </S>
    <S title="9. Security">
      <p>We use bcrypt password hashing, JWT auth over TLS, and least-privilege access controls. No system is 100% secure; please use a unique password.</p>
    </S>
    <S title="10. International">
      <p>Data may be processed in the United States and other countries where our providers operate. We use appropriate safeguards for international transfers.</p>
    </S>
    <S title="11. Changes">
      <p>We will notify you of material changes via in-app banner. Continued use means acceptance.</p>
    </S>
    <S title="12. Contact">
      <p>Email <a className="text-bx-cyan" href="mailto:privacy@bubblex.app">privacy@bubblex.app</a> or visit our <Link to="/contact" className="text-bx-cyan">contact page</Link>.</p>
    </S>
  </LegalShell>
);

export const Cookies = () => (
  <LegalShell title="Cookie Policy" lastUpdated="May 14, 2026">
    <p>BubbleX uses a minimal set of cookies and local storage to keep you signed in and remember your preferences.</p>
    <S title="What we use">
      <ul className="list-disc pl-5 space-y-1">
        <li><b>Authentication</b>: We store a JWT in your browser's localStorage as <code className="text-bx-cyan font-mono">bx_token</code>. This is essential for signed-in features.</li>
        <li><b>Preferences</b>: A cached copy of your profile (<code className="text-bx-cyan font-mono">bx_user</code>) for faster reloads.</li>
        <li><b>Stripe Checkout</b>: When purchasing Drops, Stripe sets its own session cookies on its checkout page.</li>
        <li><b>No third-party advertising or tracking cookies.</b></li>
      </ul>
    </S>
    <S title="Your choices">
      <p>You can clear your browser's storage at any time. Clearing the auth token will sign you out.</p>
    </S>
  </LegalShell>
);

export const About = () => (
  <LegalShell title="About BubbleX" lastUpdated="May 14, 2026">
    <p className="text-base sm:text-lg">
      BubbleX is a living social ecosystem built around <b>passing energy</b>. Not points. Not numbers. Real momentum that connects you, your friends, your communities, and the world.
    </p>
    <S title="Our mission">
      <p>To build the first social platform that feels alive — where every interaction has an AI personality, a story, and a measurable impact. We believe digital relationships deserve more than a like button.</p>
    </S>
    <S title="What we believe">
      <ul className="list-disc pl-5 space-y-1">
        <li><b>Energy &gt; vanity metrics.</b> Sharing should grow value, not noise.</li>
        <li><b>Transparency by default.</b> Every transfer, milestone, and donation is hash-chained on a public ledger.</li>
        <li><b>Real-world impact.</b> A share of every Drop and donation routes directly to causes our community picks.</li>
        <li><b>No gambling. No lotteries.</b> Bubbles are collectibles and identity, not investments.</li>
      </ul>
    </S>
    <S title="How it works">
      <p>You breathe a Bubble into life with an AI-generated personality and one-of-one artwork. You pass it to people through links, QR codes, or username transfers. Each pass grows its energy and velocity. Milestones trigger community Burst Events. Your Bubble evolves. You climb leaderboards. You give back through the Impact dashboard.</p>
    </S>
    <S title="Founder Drops">
      <p>Limited-supply, AI-curated legendary bubbles. A share of every sale routes directly to a charitable cause selected at checkout. <Link to="/drops" className="text-bx-cyan">Explore drops →</Link></p>
    </S>
    <S title="Get in touch">
      <p>Press, partnerships, or just want to say hi? <Link to="/contact" className="text-bx-cyan">Contact us</Link>.</p>
    </S>
  </LegalShell>
);

export const Contact = () => (
  <LegalShell title="Contact" lastUpdated="May 14, 2026">
    <p>We read every message.</p>
    <S title="Email">
      <ul className="list-disc pl-5 space-y-1">
        <li>General: <a className="text-bx-cyan" href="mailto:hello@bubblex.app">hello@bubblex.app</a></li>
        <li>Support: <a className="text-bx-cyan" href="mailto:support@bubblex.app">support@bubblex.app</a></li>
        <li>Press: <a className="text-bx-cyan" href="mailto:press@bubblex.app">press@bubblex.app</a></li>
        <li>Privacy: <a className="text-bx-cyan" href="mailto:privacy@bubblex.app">privacy@bubblex.app</a></li>
        <li>Trust & Safety: <a className="text-bx-cyan" href="mailto:safety@bubblex.app">safety@bubblex.app</a></li>
      </ul>
    </S>
    <S title="Mailing address">
      <p>BubbleX, Inc.<br />
        1 Movement Way<br />
        Houston, TX 77002<br />
        United States</p>
    </S>
    <S title="Founder Drops support">
      <p>For purchase questions, include your transaction session ID (visible at the top of your success page) and the email tied to your BubbleX account.</p>
    </S>
  </LegalShell>
);
